import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import GroupsIcon from '@mui/icons-material/Groups';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';
import React, { useMemo, useState } from 'react';
import { ContactListMember } from '../types';
import { hasDeclinedContactShare } from '../utils/contactListMembers';
import {
  createEditableTeam,
  EditableTeam,
  getAssignableContacts,
  getUnassignedMembers,
  moveMemberToTeam,
} from '../utils/teamGroups';
import styles from './ManualTeamBoard.module.css';

type ManualTeamBoardProps = {
  contacts: ContactListMember[];
  teams: EditableTeam[];
  currentUserId?: string;
  onChange: (teams: EditableTeam[]) => void;
  onSelectMember?: (member: ContactListMember, teamName?: string) => void;
};

const TEAM_PALETTES = [
  { background: '#f3efff', color: '#6d5bd0', drop: '#ece7ff' },
  { background: '#eaf1fb', color: '#3d6d99', drop: '#dce8f6' },
  { background: '#e7f5ec', color: '#2f7a54', drop: '#d8eee1' },
  { background: '#f7efe3', color: '#8a6a2b', drop: '#f1e4cf' },
];

const getInitials = (name: string) => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const MEMBER_DRAG_TYPE = 'text/plain';

type DropTarget = 'unassigned' | string;

const PersonIdentity: React.FC<{
  member: ContactListMember;
  declined: boolean;
  isYou?: boolean;
  onSelect?: (member: ContactListMember) => void;
}> = ({ member, declined, isYou, onSelect }) => {
  const content = (
    <>
      {member.avatarUrl ? (
        <img src={member.avatarUrl} alt="" className={styles.avatar} />
      ) : (
        <span className={styles.initials}>{getInitials(member.name)}</span>
      )}
      <span className={styles.personMeta}>
        <strong>
          {member.name}
          {isYou && <span className={styles.youBadge}>Deg</span>}
        </strong>
        <span>
          {declined ? 'Ingen samtykke til deling' : member.company || '—'}
        </span>
      </span>
    </>
  );

  if (!onSelect) {
    return content;
  }

  return (
    <button
      type="button"
      className={styles.personCardButton}
      onClick={() => onSelect(member)}
      aria-label={`Vis kundekort for ${member.name}`}
    >
      {content}
    </button>
  );
};

export const ManualTeamBoard: React.FC<ManualTeamBoardProps> = ({
  contacts,
  teams,
  currentUserId,
  onChange,
  onSelectMember,
}) => {
  const [query, setQuery] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [menuTeamKey, setMenuTeamKey] = useState<string | null>(null);

  const assignable = useMemo(() => getAssignableContacts(contacts), [contacts]);
  const contactsById = useMemo(
    () => new Map(contacts.map((contact) => [contact.id, contact])),
    [contacts]
  );
  const unassigned = useMemo(
    () => getUnassignedMembers(contacts, teams),
    [contacts, teams]
  );
  const filteredUnassigned = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return unassigned;
    }
    return unassigned.filter((member) => {
      const haystack = `${member.name} ${member.company ?? ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [query, unassigned]);

  const resolveMember = (id: string): ContactListMember =>
    contactsById.get(id) ?? { id, name: 'Ukjent deltaker' };

  const moveTo = (
    memberId: string,
    toTeamKey: string | 'unassigned',
    atIndex?: number
  ) => {
    onChange(moveMemberToTeam(teams, memberId, toTeamKey, atIndex));
    setDropTarget(null);
    setDraggingId(null);
  };

  const addToSmallestTeam = (memberId: string) => {
    if (teams.length === 0) {
      onChange([createEditableTeam({ index: 0, memberIds: [memberId] })]);
      return;
    }
    const target = teams.reduce((best, team) =>
      team.memberIds.length < best.memberIds.length ? team : best
    );
    moveTo(memberId, target.key);
  };

  const handleDragStart = (
    event: React.DragEvent<HTMLElement>,
    memberId: string
  ) => {
    event.dataTransfer.setData(MEMBER_DRAG_TYPE, memberId);
    event.dataTransfer.effectAllowed = 'move';
    setDraggingId(memberId);
  };

  const handleDrop = (
    event: React.DragEvent<HTMLElement>,
    target: DropTarget,
    atIndex?: number
  ) => {
    event.preventDefault();
    const memberId =
      event.dataTransfer.getData(MEMBER_DRAG_TYPE) || draggingId || '';
    if (!memberId) {
      return;
    }
    moveTo(memberId, target === 'unassigned' ? 'unassigned' : target, atIndex);
  };

  const allowDrop = (
    event: React.DragEvent<HTMLElement>,
    target: DropTarget
  ) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropTarget(target);
  };

  const deleteTeam = (teamKey: string) => {
    onChange(teams.filter((team) => team.key !== teamKey));
    setMenuTeamKey(null);
  };

  return (
    <div className={styles.board}>
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statIcon} aria-hidden="true">
            <PersonOutlineIcon />
          </span>
          <div>
            <strong>{assignable.length} deltakere totalt</strong>
            <p>
              {assignable.length === contacts.length
                ? 'Alle deltakere i sirkelen'
                : 'Deltakere som kan fordeles (uten reserverte)'}
            </p>
          </div>
        </div>
        <div className={styles.stat}>
          <span className={styles.statIcon} aria-hidden="true">
            <GroupsIcon />
          </span>
          <div>
            <strong>{teams.length} lag opprettet</strong>
            <p>Dra og slipp deltakere i ønsket lag</p>
          </div>
        </div>
        <div className={`${styles.stat} ${styles.statTip}`}>
          <span className={styles.statIcon} aria-hidden="true">
            <LightbulbOutlinedIcon />
          </span>
          <p>
            Tips: Du kan også klikke på “+” for å legge til deltaker i et lag.
          </p>
        </div>
      </div>

      <div className={styles.workspace}>
        <section className={styles.unassigned}>
          <h4>Ikke fordelt ({unassigned.length})</h4>
          <label className={styles.search}>
            <SearchIcon />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Søk etter deltaker..."
            />
          </label>
          <div className={styles.personList}>
            {filteredUnassigned.map((member) => {
              const declined = hasDeclinedContactShare(member);
              return (
                <div
                  key={member.id}
                  className={styles.person}
                  draggable
                  onDragStart={(event) => handleDragStart(event, member.id)}
                  onDragEnd={() => setDraggingId(null)}
                >
                  <PersonIdentity
                    member={member}
                    declined={declined}
                    onSelect={onSelectMember}
                  />
                  <button
                    type="button"
                    className={styles.iconAction}
                    onClick={() => addToSmallestTeam(member.id)}
                    aria-label={`Legg ${member.name} i et lag`}
                  >
                    <AddIcon />
                  </button>
                </div>
              );
            })}
          </div>
          <div
            className={
              dropTarget === 'unassigned'
                ? `${styles.dropZone} ${styles.dropZoneActive}`
                : styles.dropZone
            }
            onDragOver={(event) => allowDrop(event, 'unassigned')}
            onDragLeave={() => setDropTarget(null)}
            onDrop={(event) => handleDrop(event, 'unassigned')}
          >
            <strong>Dra hit for å fjerne fra lag</strong>
            <span>Deltakeren blir liggende i listen</span>
          </div>
        </section>

        <div className={styles.columns}>
          {teams.map((team, index) => {
            const palette = TEAM_PALETTES[index % TEAM_PALETTES.length];
            const isOver = dropTarget === team.key;
            return (
              <section
                key={team.key}
                className={styles.teamColumn}
                style={{ background: palette.background }}
                onDragOver={(event) => allowDrop(event, team.key)}
                onDragLeave={() => setDropTarget(null)}
                onDrop={(event) => handleDrop(event, team.key)}
              >
                <div
                  className={styles.teamHeader}
                  style={{ color: palette.color }}
                >
                  <GroupsIcon />
                  <input
                    className={styles.teamNameInput}
                    value={team.name}
                    onChange={(event) =>
                      onChange(
                        teams.map((item) =>
                          item.key === team.key
                            ? { ...item, name: event.target.value }
                            : item
                        )
                      )
                    }
                    aria-label={`Navn på ${team.name}`}
                  />
                  <span>({team.memberIds.length})</span>
                  <div className={styles.menuWrap}>
                    <button
                      type="button"
                      className={styles.iconAction}
                      onClick={() =>
                        setMenuTeamKey((current) =>
                          current === team.key ? null : team.key
                        )
                      }
                      aria-label={`Flere valg for ${team.name}`}
                    >
                      <MoreHorizIcon />
                    </button>
                    {menuTeamKey === team.key && (
                      <div className={styles.menu}>
                        <button
                          type="button"
                          onClick={() => deleteTeam(team.key)}
                        >
                          Slett lag
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.personList}>
                  {team.memberIds.map((memberId, memberIndex) => {
                    const member = resolveMember(memberId);
                    const isYou = member.id === currentUserId;
                    const declined = hasDeclinedContactShare(member);
                    return (
                      <div
                        key={memberId}
                        className={styles.person}
                        draggable
                        onDragStart={(event) =>
                          handleDragStart(event, memberId)
                        }
                        onDragEnd={() => setDraggingId(null)}
                        onDragOver={(event) => {
                          event.stopPropagation();
                          allowDrop(event, team.key);
                        }}
                        onDrop={(event) => {
                          event.stopPropagation();
                          handleDrop(event, team.key, memberIndex);
                        }}
                      >
                        <DragIndicatorIcon className={styles.grip} />
                        <PersonIdentity
                          member={member}
                          declined={declined}
                          isYou={isYou}
                          onSelect={
                            onSelectMember
                              ? (selected) =>
                                  onSelectMember(selected, team.name)
                              : undefined
                          }
                        />
                        <button
                          type="button"
                          className={styles.iconAction}
                          onClick={() => moveTo(memberId, 'unassigned')}
                          aria-label={`Fjern ${member.name} fra laget`}
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div
                  className={
                    isOver
                      ? `${styles.teamDrop} ${styles.dropZoneActive}`
                      : styles.teamDrop
                  }
                  style={{ borderColor: palette.drop, color: palette.color }}
                >
                  <GroupsIcon />
                  <span>
                    Dra deltakere hit
                    <br />
                    for å legge til i {team.name}
                  </span>
                </div>
              </section>
            );
          })}

          <button
            type="button"
            className={styles.addTeam}
            onClick={() =>
              onChange([...teams, createEditableTeam({ index: teams.length })])
            }
          >
            + Nytt lag
          </button>
        </div>
      </div>
    </div>
  );
};
